// Dynamic sale/discount configuration shared by the shop, the admin Sales tab
// and the server checkout path. The single source of truth is the Firestore doc
// `config/sale` (publicly readable, admin writable) so a sale can be flipped on
// without a deploy. The actual money math is enforced by Paddle: each sale is
// backed by a Paddle percentage Discount whose id is applied server-side when
// the checkout transaction is created, so client display can never change what
// a customer is charged.

import type { GncStickerName } from '@/lib/gnc-stickers';

export type SaleTheme =
    | 'blackFriday'
    | 'cyberMonday'
    | 'holiday'
    | 'newYear'
    | 'summer'
    | 'valentines'
    | 'spring'
    | 'backToSchool'
    | 'halloween'
    | 'festival'
    | 'custom';

export const SALE_THEMES: SaleTheme[] = [
    'blackFriday', 'cyberMonday', 'holiday', 'newYear', 'summer',
    'valentines', 'spring', 'backToSchool', 'halloween', 'festival', 'custom',
];

export type SalePaddleSync =
    | 'auto'    // discount created/updated in Paddle automatically via the API
    | 'manual'  // admin created the discount in the Paddle dashboard and pasted its id
    | 'error'   // automatic sync failed; sale is saved but NOT live at checkout
    | 'none';   // not synced yet (no discount id: shop shows the sale, checkout charges full price)

export type SaleSource = 'manual' | 'autopilot';

export interface SaleEvent {
    id: string;
    name: string;
    bannerText: string;
    theme: SaleTheme;
    /** Whole-number percentage off, 1-90. Percentage-only keeps client display math exact. */
    percentOff: number;
    /** Paddle package ids the sale applies to (see PADDLE_SALE_PACKAGE_IDS). */
    appliesTo: string[];
    startAtMs: number;
    endAtMs: number;
    enabled: boolean;
    /** For subscriptions: whether renewals keep the discount, or only the first payment. */
    recurring: boolean;
    paddleDiscountId: string | null;
    paddleSync: SalePaddleSync;
    paddleSyncError: string | null;
    /** 'autopilot' sales were scheduled by the yearly autopilot, not typed by hand. */
    source: SaleSource;
    /**
     * Countries this sale is for, spelled like users/{uid}.country (ipapi's
     * country_name, e.g. "South Korea"). Empty means everywhere. A country sale
     * is only shown to, and only applied at checkout for, visitors whose
     * request comes from one of these countries.
     */
    countries: string[];
    createdAtMs: number;
    updatedAtMs: number;
}

export interface SaleConfigDoc {
    sales: SaleEvent[];
    autopilot: SaleAutopilotConfig;
}

/** Every package sold through Paddle (must stay in sync with PADDLE_PRICE_MAP in actions.ts). */
export const PADDLE_SALE_PACKAGE_IDS = [
    'premium_membership',
    'elite_trial_3d',
    'lite_membership',
    'pkg_10',
    'pkg_50',
    'pkg_150',
] as const;

export const PADDLE_SALE_PACKAGE_LABELS: Record<string, string> = {
    premium_membership: 'Elite (monthly)',
    elite_trial_3d: 'Elite 7-Day Trial',
    lite_membership: 'Lite (monthly)',
    pkg_10: '10 Nano Cores',
    pkg_50: '50 Nano Cores',
    pkg_150: '150 Nano Cores',
};

export const MAX_SALE_PERCENT = 90;

function isFiniteNumber(v: unknown): v is number {
    return typeof v === 'number' && Number.isFinite(v);
}

/** Defensive parse of a raw Firestore sale entry; returns null for junk data. */
export function sanitizeSaleEvent(raw: unknown): SaleEvent | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.id !== 'string' || !r.id) return null;
    const percentOff = isFiniteNumber(r.percentOff) ? Math.round(r.percentOff) : 0;
    if (percentOff < 1 || percentOff > MAX_SALE_PERCENT) return null;
    if (!isFiniteNumber(r.startAtMs) || !isFiniteNumber(r.endAtMs) || r.endAtMs <= r.startAtMs) return null;
    const appliesTo = Array.isArray(r.appliesTo)
        ? r.appliesTo.filter((p): p is string => typeof p === 'string' && (PADDLE_SALE_PACKAGE_IDS as readonly string[]).includes(p))
        : [];
    if (appliesTo.length === 0) return null;
    const theme: SaleTheme = (SALE_THEMES as string[]).includes(r.theme as string)
        ? (r.theme as SaleTheme)
        : 'custom';
    const paddleSync: SalePaddleSync = ['auto', 'manual', 'error', 'none'].includes(r.paddleSync as string)
        ? (r.paddleSync as SalePaddleSync)
        : 'none';
    return {
        id: r.id,
        name: typeof r.name === 'string' && r.name.trim() ? r.name.trim() : 'Sale',
        bannerText: typeof r.bannerText === 'string' ? r.bannerText : '',
        theme,
        percentOff,
        appliesTo,
        startAtMs: r.startAtMs,
        endAtMs: r.endAtMs,
        enabled: r.enabled === true,
        recurring: r.recurring === true,
        paddleDiscountId: typeof r.paddleDiscountId === 'string' && r.paddleDiscountId.startsWith('dsc_') ? r.paddleDiscountId : null,
        paddleSync,
        paddleSyncError: typeof r.paddleSyncError === 'string' ? r.paddleSyncError : null,
        source: r.source === 'autopilot' ? 'autopilot' : 'manual',
        countries: sanitizeCountryList(r.countries),
        createdAtMs: isFiniteNumber(r.createdAtMs) ? r.createdAtMs : 0,
        updatedAtMs: isFiniteNumber(r.updatedAtMs) ? r.updatedAtMs : 0,
    };
}

export function sanitizeSaleConfig(raw: unknown): SaleConfigDoc {
    if (!raw || typeof raw !== 'object') return { sales: [], autopilot: defaultSaleAutopilot() };
    const record = raw as Record<string, unknown>;
    const autopilot = sanitizeSaleAutopilot(record.autopilot);
    const salesRaw = record.sales;
    if (!Array.isArray(salesRaw)) return { sales: [], autopilot };
    return {
        sales: salesRaw.map(sanitizeSaleEvent).filter((s): s is SaleEvent => s !== null),
        autopilot,
    };
}

/** Trimmed, de-duplicated country names; junk entries dropped. */
export function sanitizeCountryList(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const out: string[] = [];
    for (const entry of raw) {
        if (typeof entry !== 'string') continue;
        const name = entry.trim();
        if (name && name.length <= 60 && !out.includes(name)) out.push(name);
    }
    return out;
}

/**
 * Whether a sale reaches a visitor from `country`. A worldwide sale reaches
 * everyone; a country sale reaches nobody whose country is unknown, so a
 * failed lookup costs that visitor the local discount, never the reverse.
 */
export function saleReachesCountry(sale: Pick<SaleEvent, 'countries'>, country: string | null): boolean {
    if (sale.countries.length === 0) return true;
    return !!country && sale.countries.includes(country);
}

export function isSaleLiveNow(sale: SaleEvent, nowMs: number): boolean {
    return sale.enabled && nowMs >= sale.startAtMs && nowMs < sale.endAtMs;
}

/**
 * The sale currently shown/applied to a visitor from `country`. If windows
 * overlap, the biggest discount wins; on a tie a country sale beats a
 * worldwide one (Hanukkah over the Christmas sale in Israel), then the sale
 * ending soonest wins.
 *
 * `country` undefined skips country targeting entirely (the admin view, which
 * should see every running sale); null means the visitor's country is
 * unknown, so only worldwide sales apply.
 */
export function getActiveSale(sales: SaleEvent[], nowMs: number, country?: string | null): SaleEvent | null {
    let best: SaleEvent | null = null;
    for (const sale of sales) {
        if (!isSaleLiveNow(sale, nowMs)) continue;
        if (country !== undefined && !saleReachesCountry(sale, country)) continue;
        if (!best || sale.percentOff > best.percentOff) { best = sale; continue; }
        if (sale.percentOff < best.percentOff) continue;
        const saleLocal = sale.countries.length > 0;
        const bestLocal = best.countries.length > 0;
        if (saleLocal !== bestLocal) { if (saleLocal) best = sale; continue; }
        if (sale.endAtMs < best.endAtMs) best = sale;
    }
    return best;
}

/** True when any running sale is limited to some countries (so a visitor's country matters). */
export function hasLiveCountrySale(sales: SaleEvent[], nowMs: number): boolean {
    return sales.some(sale => sale.countries.length > 0 && isSaleLiveNow(sale, nowMs));
}

/** Paddle refuses to charge less than this (USD): "Minimum payment amount: 70". */
export const PADDLE_MIN_CHARGE_USD = 0.70;

/**
 * With the package's USD price given, a sale that would cut it under Paddle's
 * minimum charge does not apply to that package, in the shop and at checkout
 * alike (Halloween 2026: 10 Cores $0.90 at 30% = $0.63, Paddle refused it).
 */
export function saleAppliesToPackage(sale: SaleEvent, packageId: string, usdPrice?: number): boolean {
    if (!sale.appliesTo.includes(packageId)) return false;
    // shortcut: USD only, Paddle's minimum differs per currency; add a per-currency table if a localized-price buyer hits it
    return usdPrice === undefined || getDiscountedUsd(usdPrice, sale.percentOff) >= PADDLE_MIN_CHARGE_USD;
}

/** Cent-exact discounted USD amount, matching Paddle's percentage rounding. */
export function getDiscountedUsd(usdPrice: number, percentOff: number): number {
    return Math.round(usdPrice * 100 * (100 - percentOff) / 100) / 100;
}

export function formatUsd(amount: number): string {
    return `$${amount.toFixed(2)}`;
}

/**
 * ISO-4217 minor-unit exponent for a currency (USD/ILS 2, JPY/KRW 0, KWD 3).
 * Paddle reports amounts in minor units, so this is what turns one back into a
 * displayable number. Falls back to 2 for currencies Intl doesn't know.
 */
export function getCurrencyFractionDigits(currencyCode: string): number {
    try {
        const digits = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode })
            .resolvedOptions().maximumFractionDigits;
        return typeof digits === 'number' ? digits : 2;
    } catch {
        return 2;
    }
}

/** Discounted minor-unit amount, matching the cent rounding Paddle applies. */
export function getDiscountedMinor(minorAmount: number, percentOff: number): number {
    return Math.round(minorAmount * (100 - percentOff) / 100);
}

/**
 * Format a Paddle minor-unit amount in its own currency. Used for the local
 * currency line so the struck full price and the sale price are formatted
 * identically (Paddle only hands back a formatted string for the full price).
 */
export function formatMinorAmount(minorAmount: number, currencyCode: string, locale?: string): string {
    const divisor = Math.pow(10, getCurrencyFractionDigits(currencyCode));
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
            .format(minorAmount / divisor);
    } catch {
        return `${(minorAmount / divisor).toFixed(2)} ${currencyCode}`;
    }
}

/** "3d 4h" / "4h 12m" / "12m" / "under a minute" until the sale ends. */
export function formatSaleTimeLeft(sale: { endAtMs: number }, nowMs: number): string {
    const ms = sale.endAtMs - nowMs;
    if (ms <= 0) return 'ended';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'under a minute';
    const days = Math.floor(minutes / (60 * 24));
    const hours = Math.floor((minutes % (60 * 24)) / 60);
    const mins = minutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
}

export interface SaleThemeStyle {
    /** The theme's GNC sticker (src/lib/gnc-stickers.ts). */
    sticker: GncStickerName;
    /** Tailwind classes for the shop banner backdrop. */
    bannerClass: string;
    accentTextClass: string;
    /** Tailwind classes for the sale's bar in the admin calendar. */
    chipClass: string;
}

export const SALE_THEME_STYLES: Record<SaleTheme, SaleThemeStyle> = {
    blackFriday: {
        sticker: 'heartBlack',
        bannerClass: 'bg-gradient-to-r from-zinc-950 via-slate-900 to-zinc-950 border-amber-500/50',
        accentTextClass: 'text-amber-400',
        chipClass: 'bg-amber-500/25 text-amber-200 border-amber-400/50',
    },
    cyberMonday: {
        sticker: 'bolt',
        bannerClass: 'bg-gradient-to-r from-slate-950 via-cyan-950 to-slate-950 border-cyan-500/50',
        accentTextClass: 'text-cyan-400',
        chipClass: 'bg-cyan-500/25 text-cyan-200 border-cyan-400/50',
    },
    holiday: {
        sticker: 'tree',
        bannerClass: 'bg-gradient-to-r from-emerald-950 via-slate-900 to-red-950 border-emerald-500/50',
        accentTextClass: 'text-emerald-400',
        chipClass: 'bg-emerald-500/25 text-emerald-200 border-emerald-400/50',
    },
    newYear: {
        sticker: 'fireworks',
        bannerClass: 'bg-gradient-to-r from-indigo-950 via-slate-900 to-purple-950 border-indigo-400/50',
        accentTextClass: 'text-indigo-300',
        chipClass: 'bg-indigo-500/25 text-indigo-200 border-indigo-400/50',
    },
    summer: {
        sticker: 'sun',
        bannerClass: 'bg-gradient-to-r from-orange-950 via-amber-900 to-orange-950 border-orange-400/50',
        accentTextClass: 'text-orange-300',
        chipClass: 'bg-orange-500/25 text-orange-200 border-orange-400/50',
    },
    valentines: {
        sticker: 'heart',
        bannerClass: 'bg-gradient-to-r from-rose-950 via-slate-900 to-pink-950 border-rose-400/50',
        accentTextClass: 'text-rose-300',
        chipClass: 'bg-rose-500/25 text-rose-200 border-rose-400/50',
    },
    spring: {
        sticker: 'flower',
        bannerClass: 'bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border-teal-400/50',
        accentTextClass: 'text-teal-300',
        chipClass: 'bg-teal-500/25 text-teal-200 border-teal-400/50',
    },
    backToSchool: {
        sticker: 'backpack',
        bannerClass: 'bg-gradient-to-r from-sky-950 via-slate-900 to-blue-950 border-sky-400/50',
        accentTextClass: 'text-sky-300',
        chipClass: 'bg-sky-500/25 text-sky-200 border-sky-400/50',
    },
    festival: {
        sticker: 'party',
        bannerClass: 'bg-gradient-to-r from-amber-950 via-slate-900 to-fuchsia-950 border-amber-400/50',
        accentTextClass: 'text-amber-300',
        chipClass: 'bg-fuchsia-500/25 text-fuchsia-200 border-fuchsia-400/50',
    },
    halloween: {
        sticker: 'pumpkin',
        bannerClass: 'bg-gradient-to-r from-orange-950 via-zinc-950 to-purple-950 border-orange-500/50',
        accentTextClass: 'text-orange-400',
        chipClass: 'bg-orange-600/30 text-orange-200 border-orange-500/50',
    },
    custom: {
        sticker: 'tag',
        bannerClass: 'bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border-primary/50',
        accentTextClass: 'text-primary',
        chipClass: 'bg-primary/25 text-primary-foreground border-primary/50',
    },
};

// ---------------------------------------------------------------------------
// The yearly sale calendar
//
// Every recurring sale GNC runs lives in SALE_OCCASIONS below: one entry per
// occasion, with a window function that resolves that occasion's dates for any
// year. Everything else - the "New Sale" presets, the admin calendar view and
// the autopilot that schedules the whole year unattended - is derived from this
// one table, so adding an occasion here adds it everywhere at once.
// ---------------------------------------------------------------------------

export interface SaleOccasion {
    /** Stable key; part of the deterministic autopilot sale id, so never rename. */
    key: string;
    theme: SaleTheme;
    name: string;
    /** Which real-world moment this is, in plain words. The sale names are
     *  branded ("Back To Game Night"), so this is what tells you it is the
     *  back-to-school week. Admin-facing only; shoppers never see it. */
    about: string;
    bannerText: string;
    percentOff: number;
    /**
     * Countries the occasion's sale is for (see SaleEvent.countries). Omitted
     * for the worldwide sales.
     */
    countries?: string[];
    /**
     * Resolves this occasion's window for a given calendar year (UTC), or null
     * for a year whose date is not in the table (lunar and lunisolar holidays
     * move every year, so their dates are listed rather than computed).
     */
    window: (year: number) => { startAtMs: number; endAtMs: number } | null;
}

/** 4th Thursday of November (US Thanksgiving) for a given year, UTC midnight. */
export function thanksgivingUtc(year: number): Date {
    const nov1 = new Date(Date.UTC(year, 10, 1));
    const firstThursdayOffset = (4 - nov1.getUTCDay() + 7) % 7; // 4 = Thursday
    return new Date(Date.UTC(year, 10, 1 + firstThursdayOffset + 21));
}

/** 2nd Monday of October (Canadian Thanksgiving) for a given year, UTC midnight. */
function canadianThanksgivingUtcMs(year: number): number {
    const oct1 = new Date(Date.UTC(year, 9, 1));
    const firstMondayOffset = (1 - oct1.getUTCDay() + 7) % 7; // 1 = Monday
    return Date.UTC(year, 9, 1 + firstMondayOffset + 7);
}

function blackFridayStartMs(year: number): number {
    return thanksgivingUtc(year).getTime() + 24 * 3600_000;
}

const DAY_MS = 24 * 3600_000;

/** Western Easter Sunday (anonymous Gregorian algorithm), UTC midnight. */
export function easterUtcMs(year: number): number {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return Date.UTC(year, month - 1, day);
}

/**
 * Window around a holiday whose date comes from a table (month is 1-based).
 * `before` days ahead of the holiday through `after` days past it.
 */
export function tableWindow(
    table: Record<number, [number, number]>,
    before: number,
    after: number,
): (year: number) => { startAtMs: number; endAtMs: number } | null {
    return (year) => {
        const date = table[year];
        if (!date) return null;
        const dayMs = Date.UTC(year, date[0] - 1, date[1]);
        return { startAtMs: dayMs - before * DAY_MS, endAtMs: dayMs + (after + 1) * DAY_MS };
    };
}

// Holiday dates for the country sales. These follow the Hebrew, Hindu, Islamic
// and Chinese calendars, so they are looked up, not computed: extend each
// table before the last year runs out (a missing year simply schedules no
// sale). Islamic dates depend on the moon sighting and can land a day later;
// the windows are wide enough to cover that.
export const HANUKKAH_EVE: Record<number, [number, number]> = { 2026: [12, 4], 2027: [12, 24], 2028: [12, 12], 2029: [12, 1], 2030: [12, 20] };
const ROSH_HASHANA: Record<number, [number, number]> = { 2026: [9, 12], 2027: [10, 2], 2028: [9, 21], 2029: [9, 10], 2030: [9, 28] };
const PURIM: Record<number, [number, number]> = { 2026: [3, 3], 2027: [3, 23], 2028: [3, 12], 2029: [3, 1], 2030: [3, 19] };
const SEDER_NIGHT: Record<number, [number, number]> = { 2026: [4, 1], 2027: [4, 21], 2028: [4, 10], 2029: [3, 30], 2030: [4, 17] };
export const DIWALI: Record<number, [number, number]> = { 2026: [11, 8], 2027: [10, 29], 2028: [10, 17], 2029: [11, 5], 2030: [10, 26] };
export const HOLI: Record<number, [number, number]> = { 2026: [3, 4], 2027: [3, 22], 2028: [3, 11], 2029: [3, 1], 2030: [3, 20] };
const RAKSHA_BANDHAN: Record<number, [number, number]> = { 2026: [8, 28], 2027: [8, 17], 2028: [8, 5], 2029: [8, 23], 2030: [8, 13] };
export const EID_AL_FITR: Record<number, [number, number]> = { 2026: [3, 20], 2027: [3, 10], 2028: [2, 27], 2029: [2, 15], 2030: [2, 5] };
const EID_AL_ADHA: Record<number, [number, number]> = { 2026: [5, 27], 2027: [5, 16], 2028: [5, 5], 2029: [4, 24], 2030: [4, 13] };
export const LUNAR_NEW_YEAR: Record<number, [number, number]> = { 2026: [2, 17], 2027: [2, 6], 2028: [1, 26], 2029: [2, 13], 2030: [2, 3] };
const LOY_KRATHONG: Record<number, [number, number]> = { 2026: [11, 24], 2027: [11, 13], 2028: [11, 2], 2029: [11, 21], 2030: [11, 10] };
// Chuseok and the Mid-Autumn Festival fall on the same day (15th of the 8th lunar month).
const CHUSEOK: Record<number, [number, number]> = { 2026: [9, 25], 2027: [9, 15], 2028: [10, 3], 2029: [9, 22], 2030: [9, 12] };

/** The last year every holiday table above covers. */
export const COUNTRY_SALE_TABLES_END_YEAR = 2030;

export const ISRAEL = ['Israel'];
export const INDIA = ['India'];
export const MUSLIM_MAJORITY = [
    'Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman',
    'Jordan', 'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Iraq', 'Turkey',
    'Indonesia', 'Malaysia', 'Pakistan', 'Bangladesh',
];
export const LUNAR_NEW_YEAR_COUNTRIES = ['South Korea', 'Vietnam', 'Taiwan', 'Singapore', 'Hong Kong'];
const THAILAND = ['Thailand'];
const BRAZIL = ['Brazil'];
const MEXICO = ['Mexico'];
const CANADA = ['Canada'];
const JAPAN = ['Japan'];
const SINTERKLAAS_COUNTRIES = ['Netherlands', 'Belgium'];
// Western Easter countries with an Easter long weekend. Greece and the other
// Orthodox countries are left out: their Easter is usually on another date.
export const EASTER_EUROPE = [
    'United Kingdom', 'Ireland', 'Germany', 'Austria', 'Switzerland', 'France',
    'Belgium', 'Netherlands', 'Luxembourg', 'Spain', 'Portugal', 'Italy', 'Poland',
    'Czechia', 'Slovakia', 'Hungary', 'Sweden', 'Norway', 'Denmark', 'Finland',
    'Croatia', 'Slovenia',
];
// Thailand is left out: Loy Krathong lands on 11.11 in some years (13 Nov 2027).
const SINGLES_DAY_COUNTRIES = [
    'Singapore', 'Malaysia', 'Indonesia', 'Philippines', 'Vietnam',
    'Taiwan', 'Hong Kong', 'South Korea',
];

export const SALE_OCCASIONS: SaleOccasion[] = [
    {
        key: 'valentines',
        theme: 'valentines',
        name: "Valentine's Sale",
        about: "Valentine's weekend. Couples and friend groups hunting for something to do together.",
        bannerText: 'Game night for two, or for twelve. Everything discounted through the weekend.',
        percentOff: 25,
        window: (year) => ({ startAtMs: Date.UTC(year, 1, 11), endAtMs: Date.UTC(year, 1, 15) }),
    },
    {
        key: 'spring',
        theme: 'spring',
        name: 'Spring Break Sale',
        about: "Spring break: the school and university holiday week across the US and Europe.",
        bannerText: 'Break week, full lobby. Memberships and cores at spring prices.',
        percentOff: 20,
        window: (year) => ({ startAtMs: Date.UTC(year, 2, 20), endAtMs: Date.UTC(year, 2, 27) }),
    },
    {
        key: 'summer',
        theme: 'summer',
        name: 'Summer Sale',
        about: "The start of the summer holidays, when long evenings turn into long game nights.",
        bannerText: 'Long nights, big game nights. Summer prices on everything.',
        percentOff: 25,
        window: (year) => ({ startAtMs: Date.UTC(year, 6, 1), endAtMs: Date.UTC(year, 6, 8) }),
    },
    {
        key: 'backToSchool',
        theme: 'backToSchool',
        name: 'Back To Game Night',
        about: "The back-to-school week. Summer is over, everyone is home on weeknights and the regular game nights start up again.",
        bannerText: 'New season, same crew. Get everyone unlocked before the first game night.',
        percentOff: 20,
        window: (year) => ({ startAtMs: Date.UTC(year, 8, 1), endAtMs: Date.UTC(year, 8, 8) }),
    },
    {
        key: 'halloween',
        theme: 'halloween',
        name: 'Halloween Sale',
        about: "Halloween week, ending the day after. Party season, and the biggest night of the year for group games.",
        bannerText: 'Spooky season, deep discounts. Every pack, every membership.',
        percentOff: 30,
        window: (year) => ({ startAtMs: Date.UTC(year, 9, 26), endAtMs: Date.UTC(year, 10, 2) }),
    },
    {
        key: 'blackFriday',
        theme: 'blackFriday',
        name: 'Black Friday',
        about: "Black Friday week: from the Monday before US Thanksgiving through Cyber Monday, one price all week (owner, 2026-11-03: most shops start before the day, and one number is clearer in an ad).",
        bannerText: 'The biggest deal of the year. Memberships and cores at their lowest price.',
        percentOff: 40,
        // Monday 00:00 of Thanksgiving week until Cyber Monday ends (Tuesday 00:00). It covers what
        // used to be the US Thanksgiving sale and the 35% Cyber Monday sale, both folded in here.
        window: (year) => ({
            startAtMs: thanksgivingUtc(year).getTime() - 3 * DAY_MS,
            endAtMs: blackFridayStartMs(year) + 4 * DAY_MS,
        }),
    },
    {
        key: 'holiday',
        theme: 'holiday',
        name: 'Holiday Sale',
        about: "Christmas week, from the last working day before Christmas to just after Boxing Day, when family and friends are together and playing.",
        bannerText: 'Season of game nights. Gift yourself the full experience.',
        percentOff: 30,
        window: (year) => ({ startAtMs: Date.UTC(year, 11, 18), endAtMs: Date.UTC(year, 11, 27) }),
    },
    {
        key: 'newYear',
        theme: 'newYear',
        name: 'New Year Sale',
        about: "New Year's Eve into the first days of January, when resolutions and gift money get spent.",
        bannerText: 'Start the year with the whole crew unlocked.',
        percentOff: 25,
        window: (year) => ({ startAtMs: Date.UTC(year, 11, 30), endAtMs: Date.UTC(year + 1, 0, 3) }),
    },

    // Country sales: each one only shows to, and only applies at checkout for,
    // visitors from its countries. They match the language holiday pages.
    {
        key: 'roshHashana',
        theme: 'festival',
        name: 'Rosh Hashana Sale',
        about: 'Israel only. The week around Rosh Hashana, when families are together for the new year.',
        bannerText: 'Shana tova! New year, new game nights. Everything discounted this week.',
        percentOff: 20,
        countries: ISRAEL,
        window: tableWindow(ROSH_HASHANA, 5, 2),
    },
    {
        key: 'hanukkah',
        theme: 'festival',
        name: 'Hanukkah Sale',
        about: 'Israel only. All eight nights of Hanukkah.',
        bannerText: 'Eight nights of game nights. Hanukkah prices on everything.',
        percentOff: 30,
        countries: ISRAEL,
        window: tableWindow(HANUKKAH_EVE, 1, 8),
    },
    {
        key: 'purim',
        theme: 'festival',
        name: 'Purim Sale',
        about: 'Israel only. The days around Purim, party season.',
        bannerText: 'Purim party, full lobby. Memberships and cores discounted.',
        percentOff: 20,
        countries: ISRAEL,
        window: tableWindow(PURIM, 3, 2),
    },
    {
        key: 'passover',
        theme: 'festival',
        name: 'Passover Sale',
        about: 'Israel only. Seder night and the Passover holiday week, when everyone is off and at home.',
        bannerText: 'Holiday week, family at home. Passover prices on everything.',
        percentOff: 20,
        countries: ISRAEL,
        window: tableWindow(SEDER_NIGHT, 4, 4),
    },
    {
        key: 'diwali',
        theme: 'festival',
        name: 'Diwali Sale',
        about: 'India only. The Diwali week around Lakshmi Puja.',
        bannerText: 'Happy Diwali! Light up game night with festival prices.',
        percentOff: 30,
        countries: INDIA,
        window: tableWindow(DIWALI, 4, 3),
    },
    {
        key: 'holi',
        theme: 'festival',
        name: 'Holi Sale',
        about: 'India only. The days around Holi.',
        bannerText: 'Happy Holi! Colorful game nights at festival prices.',
        percentOff: 25,
        countries: INDIA,
        window: tableWindow(HOLI, 3, 2),
    },
    {
        key: 'rakshaBandhan',
        theme: 'festival',
        name: 'Raksha Bandhan Sale',
        about: 'India only. The days around Raksha Bandhan, the brothers and sisters festival.',
        bannerText: 'Game night with your siblings. Raksha Bandhan prices on everything.',
        percentOff: 20,
        countries: INDIA,
        window: tableWindow(RAKSHA_BANDHAN, 3, 2),
    },
    {
        key: 'eidAlFitr',
        theme: 'festival',
        name: 'Eid al-Fitr Sale',
        about: 'Muslim-majority countries only. The last days of Ramadan through the Eid holiday.',
        bannerText: 'Eid Mubarak! Family game nights at Eid prices.',
        percentOff: 30,
        countries: MUSLIM_MAJORITY,
        window: tableWindow(EID_AL_FITR, 3, 3),
    },
    {
        key: 'eidAlAdha',
        theme: 'festival',
        name: 'Eid al-Adha Sale',
        about: 'Muslim-majority countries only. The Eid al-Adha holiday days.',
        bannerText: 'Eid Mubarak! Holiday game nights, discounted.',
        percentOff: 25,
        countries: MUSLIM_MAJORITY,
        window: tableWindow(EID_AL_ADHA, 2, 3),
    },
    {
        key: 'lunarNewYear',
        theme: 'festival',
        name: 'Lunar New Year Sale',
        about: 'South Korea, Vietnam, Taiwan, Singapore and Hong Kong only. The Lunar New Year holiday (Seollal, Tet).',
        bannerText: 'Happy Lunar New Year! Family game nights at holiday prices.',
        percentOff: 30,
        countries: LUNAR_NEW_YEAR_COUNTRIES,
        window: tableWindow(LUNAR_NEW_YEAR, 4, 3),
    },
    {
        key: 'songkran',
        theme: 'festival',
        name: 'Songkran Sale',
        about: 'Thailand only. The Songkran new year holiday, 13 to 15 April.',
        bannerText: 'Happy Songkran! Holiday game nights, discounted.',
        percentOff: 25,
        countries: THAILAND,
        window: (year) => ({ startAtMs: Date.UTC(year, 3, 11), endAtMs: Date.UTC(year, 3, 17) }),
    },
    {
        key: 'loyKrathong',
        theme: 'festival',
        name: 'Loy Krathong Sale',
        about: 'Thailand only. The days around Loy Krathong.',
        bannerText: 'Happy Loy Krathong! Festival prices on everything.',
        percentOff: 20,
        countries: THAILAND,
        window: tableWindow(LOY_KRATHONG, 2, 2),
    },
    {
        key: 'carnaval',
        theme: 'festival',
        name: 'Carnaval Sale',
        about: 'Brazil only. Carnaval weekend through Ash Wednesday.',
        bannerText: 'Carnaval! Party games for the whole crew, discounted.',
        percentOff: 25,
        countries: BRAZIL,
        window: (year) => {
            const tuesdayMs = easterUtcMs(year) - 47 * DAY_MS;
            return { startAtMs: tuesdayMs - 4 * DAY_MS, endAtMs: tuesdayMs + 2 * DAY_MS };
        },
    },
    {
        key: 'festaJunina',
        theme: 'festival',
        name: 'Festa Junina Sale',
        about: 'Brazil only. The last week of the June festivals, up to São João on 24 June.',
        bannerText: 'Festa Junina! Arraial game nights at festival prices.',
        percentOff: 20,
        countries: BRAZIL,
        window: (year) => ({ startAtMs: Date.UTC(year, 5, 18), endAtMs: Date.UTC(year, 5, 25) }),
    },
    {
        key: 'diaDeMuertos',
        theme: 'festival',
        name: 'Día de Muertos Sale',
        about: 'Mexico only. Día de Muertos, 31 October to 2 November. Overlaps the Halloween sale; in Mexico this one wins.',
        bannerText: 'Día de Muertos: family game nights, discounted.',
        percentOff: 30,
        countries: MEXICO,
        window: (year) => ({ startAtMs: Date.UTC(year, 9, 29), endAtMs: Date.UTC(year, 10, 3) }),
    },
    {
        key: 'canadianThanksgiving',
        theme: 'festival',
        name: 'Thanksgiving Sale',
        about: 'Canada only. The Thanksgiving long weekend, Saturday through Thanksgiving Monday (the second Monday of October).',
        bannerText: 'Happy Thanksgiving! Family game nights at long-weekend prices.',
        percentOff: 25,
        countries: CANADA,
        // Saturday 00:00 through the end of Monday.
        window: (year) => {
            const mondayMs = canadianThanksgivingUtcMs(year);
            return { startAtMs: mondayMs - 2 * DAY_MS, endAtMs: mondayMs + DAY_MS };
        },
    },
    {
        key: 'sukkot',
        theme: 'festival',
        name: 'Sukkot Sale',
        about: 'Israel only. Sukkot through Simchat Torah, the school holiday week two weeks after Rosh Hashana.',
        bannerText: 'Chag sameach! Holiday week game nights, discounted.',
        percentOff: 20,
        countries: ISRAEL,
        // Erev Sukkot (Rosh Hashana + 13 days) through Simchat Torah (+21).
        window: (year) => {
            const date = ROSH_HASHANA[year];
            if (!date) return null;
            const roshHashanaMs = Date.UTC(year, date[0] - 1, date[1]);
            return { startAtMs: roshHashanaMs + 13 * DAY_MS, endAtMs: roshHashanaMs + 22 * DAY_MS };
        },
    },
    {
        key: 'easter',
        theme: 'festival',
        name: 'Easter Sale',
        about: 'Europe only (Western Easter countries). The Easter long weekend, Good Friday through Easter Monday.',
        bannerText: 'Happy Easter! Long weekend, long game nights. Everything discounted.',
        percentOff: 20,
        countries: EASTER_EUROPE,
        window: (year) => {
            const easterMs = easterUtcMs(year);
            return { startAtMs: easterMs - 2 * DAY_MS, endAtMs: easterMs + 2 * DAY_MS };
        },
    },
    {
        key: 'goldenWeek',
        theme: 'festival',
        name: 'Golden Week Sale',
        about: "Japan only. Golden Week, 29 April to 5 May, Japan's biggest holiday week.",
        bannerText: 'Golden Week! Holiday game nights at holiday prices.',
        percentOff: 25,
        countries: JAPAN,
        window: (year) => ({ startAtMs: Date.UTC(year, 3, 29), endAtMs: Date.UTC(year, 4, 6) }),
    },
    {
        key: 'japanNewYear',
        theme: 'festival',
        name: 'Year-End Sale',
        about: 'Japan only. Bōnenkai party season into the Shōgatsu new year holiday, 26 December to 3 January. 30% so it beats the worldwide Holiday and New Year sales in Japan.',
        bannerText: 'Year-end parties and New Year at home. Everything discounted.',
        percentOff: 30,
        countries: JAPAN,
        window: (year) => ({ startAtMs: Date.UTC(year, 11, 26), endAtMs: Date.UTC(year + 1, 0, 4) }),
    },
    {
        key: 'singlesDay',
        theme: 'festival',
        name: '11.11 Sale',
        about: "Southeast and East Asia only. Singles' Day, 11 November, the region's biggest online shopping day.",
        bannerText: '11.11! The biggest shopping day of the year. Everything discounted.',
        percentOff: 20,
        countries: SINGLES_DAY_COUNTRIES,
        window: (year) => ({ startAtMs: Date.UTC(year, 10, 10), endAtMs: Date.UTC(year, 10, 13) }),
    },
    {
        key: 'chuseok',
        theme: 'festival',
        name: 'Mid-Autumn Sale',
        about: 'South Korea, Vietnam, Taiwan, Singapore and Hong Kong only. Chuseok and the Mid-Autumn Festival, when families get together.',
        bannerText: 'Happy Mid-Autumn! Family game nights at festival prices.',
        percentOff: 25,
        countries: LUNAR_NEW_YEAR_COUNTRIES,
        window: tableWindow(CHUSEOK, 2, 2),
    },
    {
        key: 'sinterklaas',
        theme: 'festival',
        name: 'Sinterklaas Sale',
        about: 'Netherlands and Belgium only. Sinterklaas, 5 December (6 December in Belgium).',
        bannerText: 'Sinterklaas! Surprise the family with game night. Everything discounted.',
        percentOff: 20,
        countries: SINTERKLAAS_COUNTRIES,
        window: (year) => ({ startAtMs: Date.UTC(year, 11, 1), endAtMs: Date.UTC(year, 11, 7) }),
    },
];

export interface SalePreset {
    theme: SaleTheme;
    name: string;
    bannerText: string;
    percentOff: number;
    countries: string[];
    startAtMs: number;
    endAtMs: number;
}

/**
 * Holiday presets with real calendar windows. Any preset whose window already
 * ended relative to `nowMs` rolls forward to next year, so the buttons always
 * schedule the upcoming occurrence.
 */
export function buildSalePresets(nowMs: number): SalePreset[] {
    const year = new Date(nowMs).getUTCFullYear();
    const presets: SalePreset[] = [];
    for (const occasion of SALE_OCCASIONS) {
        const thisYear = occasion.window(year);
        const win = thisYear && thisYear.endAtMs > nowMs ? thisYear : occasion.window(year + 1);
        if (!win) continue;
        presets.push({
            theme: occasion.theme,
            name: occasion.name,
            bannerText: occasion.bannerText,
            percentOff: occasion.percentOff,
            countries: [...(occasion.countries || [])],
            startAtMs: win.startAtMs,
            endAtMs: win.endAtMs,
        });
    }
    return presets;
}

/** One dated occurrence of an occasion. */
export interface PlannedSale {
    /** Deterministic id, so re-running the autopilot never duplicates a sale. */
    id: string;
    occasionKey: string;
    year: number;
    theme: SaleTheme;
    name: string;
    about: string;
    bannerText: string;
    percentOff: number;
    countries: string[];
    startAtMs: number;
    endAtMs: number;
}

/** Deterministic id for an autopilot-scheduled occurrence. */
export function autopilotSaleId(occasionKey: string, year: number): string {
    return `auto_${occasionKey}_${year}`;
}

export function isAutopilotSaleId(id: string): boolean {
    return id.startsWith('auto_');
}

/**
 * The occasion behind a calendar sale id ("auto_holiday_2026" -> the Christmas
 * week entry), so a sale that is already scheduled can still be explained.
 * Returns null for hand-made sales, which have ids of their own.
 */
export function getOccasionForSaleId(saleId: string): SaleOccasion | null {
    if (!isAutopilotSaleId(saleId)) return null;
    const key = saleId.slice('auto_'.length).replace(/_\d+$/, '');
    return SALE_OCCASIONS.find(o => o.key === key) || null;
}

/**
 * Every occasion occurrence whose window has not fully passed, from `nowMs`
 * through `horizonDays` ahead, oldest first.
 */
export function buildSaleCalendar(nowMs: number, horizonDays: number): PlannedSale[] {
    const limitMs = nowMs + horizonDays * 24 * 3600_000;
    const startYear = new Date(nowMs).getUTCFullYear() - 1;
    const endYear = new Date(limitMs).getUTCFullYear() + 1;
    const out: PlannedSale[] = [];
    for (let year = startYear; year <= endYear; year++) {
        for (const occasion of SALE_OCCASIONS) {
            const win = occasion.window(year);
            if (!win || win.endAtMs <= nowMs || win.startAtMs > limitMs) continue;
            out.push({
                id: autopilotSaleId(occasion.key, year),
                occasionKey: occasion.key,
                year,
                theme: occasion.theme,
                name: occasion.name,
                about: occasion.about,
                bannerText: occasion.bannerText,
                percentOff: occasion.percentOff,
                countries: [...(occasion.countries || [])],
                startAtMs: win.startAtMs,
                endAtMs: win.endAtMs,
            });
        }
    }
    return out.sort((a, b) => a.startAtMs - b.startAtMs);
}

// ---------------------------------------------------------------------------
// Autopilot
//
// With autopilot on, the whole calendar above is scheduled ahead of time: each
// occurrence becomes a real, Paddle-synced sale that switches itself on and off
// at the right moment. Nothing has to be pressed for a sale to run.
// ---------------------------------------------------------------------------

export interface SaleAutopilotConfig {
    enabled: boolean;
    /** Packages every autopilot sale discounts. */
    appliesTo: string[];
    /** Whether subscription renewals keep the discount (off = first payment only). */
    recurring: boolean;
    /** How far ahead occurrences are scheduled. A full year+ means a year of hands-off sales. */
    horizonDays: number;
    /** Autopilot sale ids the admin deleted; never scheduled again. */
    skippedIds: string[];
    lastRunMs: number;
    lastRunError: string | null;
    lastCreatedIds: string[];
}

export const AUTOPILOT_DEFAULT_HORIZON_DAYS = 400;
export const AUTOPILOT_MIN_HORIZON_DAYS = 30;
export const AUTOPILOT_MAX_HORIZON_DAYS = 800;

export function defaultSaleAutopilot(): SaleAutopilotConfig {
    return {
        enabled: false,
        appliesTo: [...PADDLE_SALE_PACKAGE_IDS],
        recurring: false,
        horizonDays: AUTOPILOT_DEFAULT_HORIZON_DAYS,
        skippedIds: [],
        lastRunMs: 0,
        lastRunError: null,
        lastCreatedIds: [],
    };
}

export function sanitizeSaleAutopilot(raw: unknown): SaleAutopilotConfig {
    const base = defaultSaleAutopilot();
    if (!raw || typeof raw !== 'object') return base;
    const r = raw as Record<string, unknown>;
    const appliesTo = Array.isArray(r.appliesTo)
        ? r.appliesTo.filter((p): p is string => typeof p === 'string' && (PADDLE_SALE_PACKAGE_IDS as readonly string[]).includes(p))
        : [];
    const horizon = isFiniteNumber(r.horizonDays) ? Math.round(r.horizonDays) : base.horizonDays;
    return {
        enabled: r.enabled === true,
        appliesTo: appliesTo.length > 0 ? appliesTo : base.appliesTo,
        recurring: r.recurring === true,
        horizonDays: Math.max(AUTOPILOT_MIN_HORIZON_DAYS, Math.min(AUTOPILOT_MAX_HORIZON_DAYS, horizon)),
        skippedIds: Array.isArray(r.skippedIds) ? r.skippedIds.filter((id): id is string => typeof id === 'string') : [],
        lastRunMs: isFiniteNumber(r.lastRunMs) ? r.lastRunMs : 0,
        lastRunError: typeof r.lastRunError === 'string' ? r.lastRunError : null,
        lastCreatedIds: Array.isArray(r.lastCreatedIds) ? r.lastCreatedIds.filter((id): id is string => typeof id === 'string') : [],
    };
}

function windowsOverlap(a: { startAtMs: number; endAtMs: number }, b: { startAtMs: number; endAtMs: number }): boolean {
    return a.startAtMs < b.endAtMs && b.startAtMs < a.endAtMs;
}

/**
 * Whether two sales compete for the same shoppers. Two worldwide sales do; two
 * country sales do when they share a country. A country sale never competes
 * with a worldwide one: where both run, the bigger discount wins for that
 * country (see getActiveSale), and everyone else keeps the worldwide sale.
 */
export function audiencesClash(a: { countries: string[] }, b: { countries: string[] }): boolean {
    if (a.countries.length === 0 || b.countries.length === 0) {
        return a.countries.length === 0 && b.countries.length === 0;
    }
    return a.countries.some(c => b.countries.includes(c));
}

export type PlanSkipReason = 'exists' | 'skipped' | 'overlap';

export interface AutopilotPlan {
    /** Occurrences that will be created on the next autopilot run. */
    toCreate: PlannedSale[];
    /** Occurrences that will not be created, with the reason why. */
    skipped: Array<{ plan: PlannedSale; reason: PlanSkipReason }>;
    /** Its own future sales whose occasion now has other dates or another percent in SALE_OCCASIONS. */
    toUpdate: Array<{ plan: PlannedSale; sale: SaleEvent }>;
    /** Its own future sales whose occasion was taken out of SALE_OCCASIONS (folded into another). */
    toRemove: SaleEvent[];
}

/**
 * A sale only the autopilot ever wrote and that has not started: it may be moved or removed when the
 * calendar table changes. The autopilot writes createdAtMs === updatedAtMs; any save from the Sales
 * tab stamps a later updatedAtMs, and a hand-edited sale is never touched again.
 */
export function isUntouchedFutureAutopilotSale(sale: SaleEvent, nowMs: number): boolean {
    return sale.source === 'autopilot' && isAutopilotSaleId(sale.id) && sale.updatedAtMs === sale.createdAtMs && sale.startAtMs > nowMs;
}

/**
 * What the autopilot would do right now. Pure, so the admin preview and the
 * server run can never disagree about which sales get scheduled.
 *
 * An occurrence is skipped when a sale with the same id already exists (the
 * run is idempotent), when the admin deleted that occurrence (skippedIds), or
 * when it would overlap a sale that is already scheduled - a hand-made sale
 * always wins over an automatic one. An existing one it wrote itself and nobody
 * edited is brought in line with the table before it starts (owner, 2026-11-03:
 * the calendar should follow a change on its own, not by hand), and one whose
 * occasion left the table is removed.
 */
export function planAutopilotSales(
    sales: SaleEvent[],
    autopilot: SaleAutopilotConfig,
    nowMs: number,
): AutopilotPlan {
    const byId = new Map(sales.map(s => [s.id, s]));
    const skippedIds = new Set(autopilot.skippedIds);
    const calendar = buildSaleCalendar(nowMs, autopilot.horizonDays);
    const toCreate: PlannedSale[] = [];
    const skipped: AutopilotPlan['skipped'] = [];
    const toUpdate: AutopilotPlan['toUpdate'] = [];
    const keys = new Set(SALE_OCCASIONS.map(o => o.key));
    // "auto_<key>_<year>": the key is everything between the prefix and the year
    const toRemove = sales.filter(s => isUntouchedFutureAutopilotSale(s, nowMs) && !keys.has(s.id.slice(5, s.id.lastIndexOf('_'))));
    const removed = new Set(toRemove.map(s => s.id));
    // Overlap is checked against the sales that exist plus the ones this run
    // adds, so two occurrences can never be scheduled on top of each other.
    const claimed: Array<{ id: string; startAtMs: number; endAtMs: number; countries: string[] }> = sales
        .filter(s => s.enabled && !removed.has(s.id))
        .map(s => ({ id: s.id, startAtMs: s.startAtMs, endAtMs: s.endAtMs, countries: s.countries }));
    const clashes = (plan: PlannedSale, self?: string) => claimed.some(w => w.id !== self && windowsOverlap(w, plan) && audiencesClash(w, plan));

    for (const plan of calendar) {
        const existing = byId.get(plan.id);
        if (existing) {
            const changed = existing.startAtMs !== plan.startAtMs || existing.endAtMs !== plan.endAtMs || existing.percentOff !== plan.percentOff;
            if (changed && isUntouchedFutureAutopilotSale(existing, nowMs) && !clashes(plan, existing.id)) {
                toUpdate.push({ plan, sale: existing });
                const own = claimed.find(w => w.id === existing.id);
                if (own) Object.assign(own, { startAtMs: plan.startAtMs, endAtMs: plan.endAtMs });
            } else {
                skipped.push({ plan, reason: 'exists' });
            }
            continue;
        }
        if (skippedIds.has(plan.id)) { skipped.push({ plan, reason: 'skipped' }); continue; }
        if (clashes(plan)) { skipped.push({ plan, reason: 'overlap' }); continue; }
        toCreate.push(plan);
        claimed.push({ id: plan.id, startAtMs: plan.startAtMs, endAtMs: plan.endAtMs, countries: plan.countries });
    }
    return { toCreate, skipped, toUpdate, toRemove };
}

/** Turns a planned occurrence into a savable sale (Paddle sync happens server-side). */
export function plannedSaleToEvent(
    plan: PlannedSale,
    autopilot: SaleAutopilotConfig,
    nowMs: number,
): SaleEvent {
    return {
        id: plan.id,
        name: plan.name,
        bannerText: plan.bannerText,
        theme: plan.theme,
        percentOff: plan.percentOff,
        appliesTo: [...autopilot.appliesTo],
        startAtMs: plan.startAtMs,
        endAtMs: plan.endAtMs,
        enabled: true,
        recurring: autopilot.recurring,
        paddleDiscountId: null,
        paddleSync: 'none',
        paddleSyncError: null,
        source: 'autopilot',
        countries: [...plan.countries],
        createdAtMs: nowMs,
        updatedAtMs: nowMs,
    };
}

/** The next sale that has not started yet, soonest first. */
export function getNextUpcomingSale(sales: SaleEvent[], nowMs: number): SaleEvent | null {
    return sales
        .filter(s => s.enabled && s.startAtMs > nowMs)
        .sort((a, b) => a.startAtMs - b.startAtMs)[0] || null;
}

/** "in 3 days" / "in 4 hours" / "in 2 months" - coarse lead time to a sale start. */
export function formatSaleLeadTime(startAtMs: number, nowMs: number): string {
    const ms = startAtMs - nowMs;
    if (ms <= 0) return 'now';
    const hours = Math.floor(ms / 3600_000);
    if (hours < 1) return 'in under an hour';
    if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
    const days = Math.round(ms / (24 * 3600_000));
    if (days < 45) return `in ${days} day${days === 1 ? '' : 's'}`;
    const months = Math.round(days / 30);
    return `in ${months} month${months === 1 ? '' : 's'}`;
}

/**
 * How far ahead the schedule is covered: the end of the last scheduled sale.
 * Returns 0 when nothing upcoming is scheduled.
 */
export function getScheduleCoverageEndMs(sales: SaleEvent[], nowMs: number): number {
    return sales
        .filter(s => s.enabled && s.endAtMs > nowMs)
        .reduce((max, s) => Math.max(max, s.endAtMs), 0);
}

/**
 * Starting the free Elite trial during a sale saves that sale's price for the
 * player (owner, 2026-10-28): "keep it" after the trial still charges the saved
 * percent, for ELITE_TRIAL_SALE_LOCK_DAYS from the start, even when the sale
 * ended before the trial did. startCardlessEliteTrial writes it to
 * users/{uid}.eliteTrialSale (a server-owned field); the shop shows it, and the
 * checkout applies it through the sale's own Paddle discount, whose expiry runs
 * this long past the sale end (admin-actions, the sale's discountBody).
 */
export const ELITE_TRIAL_SALE_LOCK_DAYS = 30;

export interface EliteTrialSaleLock {
    saleId: string;
    name: string;
    percentOff: number;
    recurring: boolean;
    lockedAtMs: number;
    validUntilMs: number;
}

export function buildEliteTrialSaleLock(sale: SaleEvent, nowMs: number): EliteTrialSaleLock {
    return {
        saleId: sale.id,
        name: sale.name,
        percentOff: sale.percentOff,
        recurring: sale.recurring === true,
        lockedAtMs: nowMs,
        validUntilMs: nowMs + ELITE_TRIAL_SALE_LOCK_DAYS * 86_400_000,
    };
}

/** The saved sale while it still counts, or null; `raw` is user-doc data of any shape. */
export function validEliteTrialSaleLock(raw: unknown, nowMs: number): EliteTrialSaleLock | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    if (typeof r.saleId !== 'string' || typeof r.name !== 'string') return null;
    if (typeof r.percentOff !== 'number' || !(r.percentOff > 0 && r.percentOff <= 90)) return null;
    if (typeof r.validUntilMs !== 'number' || !(r.validUntilMs > nowMs)) return null;
    return {
        saleId: r.saleId,
        name: r.name,
        percentOff: r.percentOff,
        recurring: r.recurring === true,
        lockedAtMs: typeof r.lockedAtMs === 'number' ? r.lockedAtMs : 0,
        validUntilMs: r.validUntilMs,
    };
}

/**
 * At checkout: the admin-configured sale a valid lock points at, so the percent
 * and the Paddle discount come from config/sale and never from the user doc.
 */
export function lockedTrialSale(raw: unknown, sales: SaleEvent[], nowMs: number): SaleEvent | null {
    const lock = validEliteTrialSaleLock(raw, nowMs);
    if (!lock) return null;
    return sales.find(s => s.id === lock.saleId && !!s.paddleDiscountId) || null;
}
